import { useEffect, useRef } from 'react';
import type { ChatMessage } from '../../types/chat';

function formatContent(text: string) {
  // Simple inline markdown: **bold**, `code`, ```code blocks```, - bullet lists
  const lines = text.split('\n');
  const elements: (string | JSX.Element)[] = [];
  let inCodeBlock = false;
  let codeLines: string[] = [];
  let key = 0;

  for (const line of lines) {
    if (line.startsWith('```')) {
      if (inCodeBlock) {
        elements.push(
          <pre key={key++} className="bg-gray-800 text-gray-100 text-xs rounded p-2 my-1 overflow-x-auto whitespace-pre-wrap">
            {codeLines.join('\n')}
          </pre>
        );
        codeLines = [];
        inCodeBlock = false;
      } else {
        inCodeBlock = true;
      }
      continue;
    }

    if (inCodeBlock) {
      codeLines.push(line);
      continue;
    }

    // Bullet lists
    if (line.match(/^[-*]\s/)) {
      elements.push(
        <div key={key++} className="flex gap-1.5 ml-2">
          <span className="text-gray-400 shrink-0">&bull;</span>
          <span>{formatInline(line.slice(2))}</span>
        </div>
      );
      continue;
    }

    // Numbered lists
    const numMatch = line.match(/^(\d+)[.)]\s/);
    if (numMatch) {
      elements.push(
        <div key={key++} className="flex gap-1.5 ml-2">
          <span className="text-gray-400 shrink-0">{numMatch[1]}.</span>
          <span>{formatInline(line.slice(numMatch[0].length))}</span>
        </div>
      );
      continue;
    }

    // Empty line = paragraph break
    if (!line.trim()) {
      elements.push(<div key={key++} className="h-2" />);
      continue;
    }

    elements.push(<div key={key++}>{formatInline(line)}</div>);
  }

  // Handle unclosed code block
  if (inCodeBlock && codeLines.length > 0) {
    elements.push(
      <pre key={key++} className="bg-gray-800 text-gray-100 text-xs rounded p-2 my-1 overflow-x-auto whitespace-pre-wrap">
        {codeLines.join('\n')}
      </pre>
    );
  }

  return elements;
}

function formatInline(text: string): (string | JSX.Element)[] {
  const parts: (string | JSX.Element)[] = [];
  // Match **bold** and `inline code`
  const regex = /(\*\*(.+?)\*\*)|(`([^`]+)`)/g;
  let lastIndex = 0;
  let match: RegExpExecArray | null;
  let key = 0;

  while ((match = regex.exec(text)) !== null) {
    if (match.index > lastIndex) {
      parts.push(text.slice(lastIndex, match.index));
    }
    if (match[2]) {
      parts.push(<strong key={key++}>{match[2]}</strong>);
    } else if (match[4]) {
      parts.push(
        <code key={key++} className="bg-gray-200 text-gray-800 px-1 rounded text-xs">
          {match[4]}
        </code>
      );
    }
    lastIndex = match.index + match[0].length;
  }
  if (lastIndex < text.length) {
    parts.push(text.slice(lastIndex));
  }
  return parts.length > 0 ? parts : [text];
}

export default function ChatMessages({ messages }: { messages: ChatMessage[] }) {
  const bottomRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    bottomRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  if (messages.length === 0) {
    return (
      <div className="flex-1 flex items-center justify-center p-6 text-gray-400 text-sm text-center">
        <div>
          <div className="text-2xl mb-2">💬</div>
          <div>Ask me anything about your EEP data</div>
          <div className="text-xs mt-1 text-gray-300">Revenue, funnel metrics, retention, marketing...</div>
        </div>
      </div>
    );
  }

  return (
    <div className="flex-1 overflow-y-auto p-3 space-y-3">
      {messages.map((msg) => (
        <div
          key={msg.id}
          className={`flex ${msg.role === 'user' ? 'justify-end' : 'justify-start'}`}
        >
          <div
            className={`max-w-[85%] rounded-xl px-3 py-2 text-sm leading-relaxed ${
              msg.role === 'user'
                ? 'bg-eep-navy text-white'
                : 'bg-gray-100 text-gray-800'
            }`}
          >
            {msg.role === 'assistant' ? (
              <div className="space-y-0.5">
                {msg.isStreaming && !msg.content ? (
                  <div className="flex items-center gap-1 py-1">
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '0ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '150ms' }} />
                    <span className="w-1.5 h-1.5 rounded-full bg-gray-400 animate-bounce" style={{ animationDelay: '300ms' }} />
                  </div>
                ) : (
                  <>
                    {formatContent(msg.content)}
                    {msg.isStreaming && (
                      <span className="inline-block w-1.5 h-4 bg-eep-navy/60 animate-pulse ml-0.5 align-middle" />
                    )}
                  </>
                )}
              </div>
            ) : (
              msg.content
            )}
          </div>
        </div>
      ))}
      <div ref={bottomRef} />
    </div>
  );
}
